/**
 * SessionManager
 * 
 * セッション管理UI統合コンポーネント
 * 期限切れ・認証エラーの統合管理
 * @author @developer
 * @date 2025-09-05
 */

import React, { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertCircle as ErrorIcon,
  LogOut as LogoutIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuthContext } from '../../hooks/useAuth';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * SessionManagerコンポーネント
 * 
 * 機能:
 * 1. セッション期限切れダイアログ表示
 * 2. 認証エラー通知（Snackbar）
 * 3. 自動ログアウト警告（将来対応）
 */
const SessionManager: React.FC = () => {
  const { error, clearError } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;

  // ログイン・登録ページではSessionManagerのエラー表示を無効化
  // （各ページが独自のエラー表示を持つため）
  const isAuthPage = currentPath === '/login' || currentPath === '/register' || currentPath.startsWith('/email-verification');

  // Issue #1280: 型付きエラー検出（文字列ベースの脆弱な検出を廃止）
  const isSessionExpired = error?.type === 'SESSION_EXPIRED';

  // その他の認証エラーをToastで表示（useEffectは常にトップレベルで呼ぶ）
  useEffect(() => {
    // 認証ページでは何もしない
    if (isAuthPage) return;

    if (error && !isSessionExpired) {
      toast.error('認証エラー', {
        description: error.message,
        duration: 6000,
      });
      // Toast表示後、エラーをクリア
      const timer = setTimeout(() => {
        clearError();
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [error, isSessionExpired, clearError, isAuthPage]);

  // ログイン画面へのリダイレクト処理
  const handleLoginRedirect = () => {
    clearError();
    navigate('/login', { replace: true });
  };

  // 認証ページでは何も表示しない
  if (isAuthPage) {
    return null;
  }
  
  return (
    <>
      {/* セッション期限切れダイアログ */}
      <Dialog open={isSessionExpired} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ErrorIcon className="h-5 w-5 text-destructive" />
              セッションが期限切れです
            </DialogTitle>
            <DialogDescription className="space-y-2 pt-2">
              <p>セキュリティのため、セッションの有効期限が切れました。</p>
              <p className="text-sm text-muted-foreground">
                再度ログインして続行してください。
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={handleLoginRedirect} className="w-full">
              <LogoutIcon className="mr-2 h-4 w-4" />
              ログイン画面へ移動
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* その他の認証エラー通知はuseEffectでToast表示 */}

      {/* 自動ログアウト警告（将来実装） */}
      {/*
      将来的にToastまたはAlertで実装予定
      */}
    </>
  );
};

export default SessionManager;